import type {NavigateFunction} from 'react-router-dom';
import {useNavigate} from 'react-router-dom';
import {useCallback} from 'react';

import {AccessRequestDecision} from '~shared/models';

import {
    parsePath,
    getNormalizedPath,
    removeTrailingSlash,
    removeSearchParams,
    setSearchParams,
} from '~ui/utils';

export enum DialogType {
    Create = 'create',
    Settings = 'settings',
    Subscribe = 'subscribe',
    Delete = 'delete',
    Move = 'move',
    Copy = 'copy',
    Actuality = 'obsolete',
    Convert = 'convert',
}

export type CreatePageDialogType = 'general' | 'subpage' | 'current';
export type ActualityDialogType = 'actual' | 'obsolete';
export type SettingsDialogTab = 'authors' | 'access' | 'common';

type CreatePageParams = {clusterSlug?: string; type: CreatePageDialogType};
type SettingsParams = {
    tabId: SettingsDialogTab;
    hideAuthors?: boolean;
    hideAccess?: boolean;
    hideRedirect?: boolean;
};
type DeleteDialogParams = {pageSlug: string};
type RequestAccessDialogParams = {id: string; action: AccessRequestDecision};
type ActualityDialogParams = {type: ActualityDialogType};

type DialogParamsKeys =
    | keyof CreatePageParams
    | keyof SettingsParams
    | keyof DeleteDialogParams
    | keyof RequestAccessDialogParams
    | keyof ActualityDialogParams;

const dialogParams: DialogParamsKeys[] = [
    'tabId',
    'hideAuthors',
    'hideAccess',
    'hideRedirect',
    'clusterSlug',
    'pageSlug',
    'type',
    'action',
    'id',
];

function closeDialogBase(navigate: NavigateFunction) {
    const {handler, pathname} = parsePath(window.location.pathname);
    const newSearch = removeSearchParams(window.location.search, dialogParams);

    if (handler) {
        navigate({
            pathname: getNormalizedPath(pathname),
            search: newSearch,
        });
    }
}

function openDialogBase(
    navigate: NavigateFunction,
    type: DialogType,
    params?: Record<string, string | boolean>,
) {
    const {pathname, handler} = parsePath(window.location.pathname);
    const formattedPath = removeTrailingSlash(pathname);

    const searchParams = params
        ? setSearchParams(params, window.location.search)
        : new URLSearchParams(window.location.search).toString();
    const search = searchParams ? `?${searchParams}` : '';

    if (handler === `.${type}`) {
        return;
    }

    navigate({
        pathname: `${formattedPath}/.${type}`,
        search,
    });
}

type OpenDialog = {
    (type: DialogType.Convert): void;
    (type: DialogType.Subscribe): void;
    (type: DialogType.Move): void;
    (type: DialogType.Copy): void;
    (type: DialogType.Delete, params?: DeleteDialogParams): void;
    (type: DialogType.Create, params?: CreatePageParams): void;
    (type: DialogType.Settings, params?: SettingsParams): void;
    (type: DialogType.Actuality, params?: ActualityDialogParams): void;
};

export function useDialog() {
    const navigate = useNavigate();

    const openDialog: OpenDialog = (
        type: DialogType,
        params?: Record<string, string | boolean>,
    ) => {
        return openDialogBase(navigate, type, params);
    };

    const handleOpenDialog = useCallback(openDialog, [navigate]);

    const handleCloseDialog = useCallback(() => closeDialogBase(navigate), [navigate]);

    return {openDialog: handleOpenDialog, closeDialog: handleCloseDialog};
}
